import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { fetchAuthMe } from './api.js';

export const AUTH_ME_QUERY_KEY = ['auth', 'me'] as const;

/**
 * Resolves the caller's local (Neon) identity -- roles, permissions,
 * account status -- by calling GET /api/v1/auth/me with the current Clerk
 * session token. This is the *only* source the frontend trusts for
 * authorization display (e.g. admin nav visibility, see UserMenu): never
 * Clerk's publicMetadata, never a frontend constant. The Worker
 * independently re-enforces every check server-side regardless of what
 * this hook returns -- see docs/security-model.md "Frontend guards are UX
 * only".
 *
 * `enabled` gates the query on Clerk having finished loading *and* the
 * user actually being signed in, so a signed-out visitor never fires a
 * request that would just come back 401, and a not-yet-loaded Clerk state
 * never fires a request with a token that isn't ready yet.
 */
export function useAppUser() {
  const { isLoaded, isSignedIn, getToken } = useAuth();

  const query = useQuery({
    queryKey: AUTH_ME_QUERY_KEY,
    queryFn: async () => {
      const token = await getToken();
      if (!token) {
        // Clerk reported isSignedIn but couldn't produce a token -- treat
        // as an expired/invalidated session rather than a generic failure,
        // so callers can distinguish "try signing in again" from "backend
        // is down".
        throw new Error('SESSION_EXPIRED');
      }
      return fetchAuthMe(token);
    },
    enabled: isLoaded && isSignedIn === true,
    staleTime: 30_000,
    retry: 1,
  });

  return {
    ...query,
    clerkLoaded: isLoaded,
    isSignedIn: isSignedIn ?? false,
  };
}
